import { useCallback, useEffect, useState } from 'react'
import { api, type NotificationRecord } from '../api'
import { Icon } from './Icon'
import { useDialog } from '../useDialog'

/**
 * "Somebody is waiting on you", where a person will see it.
 *
 * A count that only ever grows teaches people to ignore it, so opening the
 * panel marks everything read — the panel IS the reading. Each row is a door:
 * a mention or a reply goes to the page it was left on, a review to the draft
 * it is about. Nothing here is a mail replacement; it is what can honestly
 * reach somebody today.
 */
/**
 * What each row IS, in a word.
 *
 * Every kind rendered as a bare subject line, so a page falling due and
 * somebody mentioning you looked identical — and three of the kinds this
 * panel can receive were added after its copy was written, which said it
 * carried "mentions, replies and reviews".
 *
 * A word rather than an icon: ten kinds is more than an icon set can
 * distinguish at 13px, and the word is what somebody scanning actually reads.
 */
/**
 * Which section of Settings a notification is really about.
 *
 * "Everything else goes to Settings" was true when Settings was one scroll of
 * twenty-one cards. It has three subjects and a section list now, so landing
 * on its front page hands somebody a screen and asks them to find the card the
 * message was about — which for a failed payment is two clicks and a guess.
 * A kind with no entry lands on the front page, which is the honest default.
 */
const SETTLED_IN: Record<string, string | undefined> = {
  payment_failed: 'org/plan',
  plan_ended: 'org/plan',
  ai_allowance: 'site/general',
  webhook_disabled: 'org/webhooks',
  domain_verified: 'site/domain',
  owner_changed: 'org/general',
}

const KIND: Record<string, string> = {
  mention: 'Mentioned',
  reply: 'Reply',
  comment: 'Comment',
  impact: 'API changed',
  review_requested: 'Review asked',
  review_verdict: 'Review',
  published: 'Published',
  publish_failed: 'Publish failed',
  webhook_disabled: 'Webhook off',
  review_due: 'Due for review',
  domain_verified: 'Domain proved',
  // Most of today's AI allowance is spent, told to whoever can decide about it.
  ai_allowance: 'Allowance nearly spent',
  plan_ended: 'Plan ended',
  payment_failed: 'Payment failed',
  conflict: 'Version replaced',
  owner_changed: 'Owner changed',
  certificate_failed: 'Certificate not issued',
}

export function Notifications({
  onOpenPage,
  onOpenDraft,
  onOpenSettings,
}: {
  onOpenPage: (path: string, branch: string | null) => void
  onOpenDraft: (branch: string) => void
  /** Where the kinds that are about the workspace lead. See the row below. */
  /** Settings, at the section the notification is about — see `SETTLED_IN`. */
  onOpenSettings: (at?: string) => void
}) {
  const [rows, setRows] = useState<NotificationRecord[] | null>(null)
  /**
   * The read that did not come back, kept apart from the read that has not
   * come back yet.
   *
   * A failure used to be written as an empty list, and an empty list is a
   * sentence: "Nothing yet. When somebody names you in a comment…". So a
   * workspace that could not answer told a person, in as many words, that
   * nobody had asked them for anything — which is the one thing this panel
   * exists to be trusted about.
   */
  const [failed, setFailed] = useState(false)
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const panel = useDialog<HTMLElement>(open, () => setOpen(false))

  const load = useCallback(async () => {
    try {
      const result = await api.notifications()
      setRows(result.notifications)
      setUnread(result.unread)
      setFailed(false)
    } catch {
      // A workspace that cannot answer is not worth a broken top bar, so the
      // BUTTON stays quiet — but the panel behind it says what happened.
      setFailed(true)
      setUnread(0)
    }
  }, [])

  useEffect(() => {
    void load()
    // Slow on purpose: this is a courtesy, not a chat client, and a tight
    // poll would cost every open tab a request a second for news that
    // arrives a few times a day.
    const timer = window.setInterval(() => void load(), 60_000)
    return () => window.clearInterval(timer)
  }, [load])

  const show = async () => {
    setOpen(true)
    if (unread > 0) {
      setUnread(0)
      try {
        await api.markNotificationsRead()
        await load()
      } catch {
        /* the panel is open either way */
      }
    }
  }

  return (
    <>
      <button
        className="btn btn--ghost btn--icon btn--sm"
        data-tip={unread > 0 ? `${unread} waiting for you` : 'Nothing waiting'}
        data-notifications
        onClick={() => void show()}
      >
        <Icon name="alert" size={15} label="Notifications" />
        {unread > 0 && <span className="badge badge--warn">{unread}</span>}
      </button>

      {open && (
        <div className="scrim" onMouseDown={() => setOpen(false)}>
          <aside
            className="drawer"
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header className="drawer__head">
              <div>
                <h2 className="drawer__title">Waiting for you</h2>
                {/*
                  Two families rather than a list of kinds.

                  This sentence has rotted twice — it said "mentions, replies
                  and reviews" when the panel could show eight kinds, and named
                  six when it could show fourteen. A list of kinds in prose is a
                  list that goes stale every time the product learns to say
                  something, so this says what the two halves ARE.
                */}
                <p className="drawer__sub">
                  What somebody sent you, and what this product noticed on its own.
                </p>
              </div>
              <button className="btn btn--ghost btn--icon" onClick={() => setOpen(false)}>
                <Icon name="close" size={15} label="Close" />
              </button>
            </header>
            <div className="drawer__body">
              {failed ? (
                <p className="screen__error">
                  <Icon name="alert" size={13} /> Your notifications could not be read, so this
                  panel is not saying there are none. It tries again every minute.
                </p>
              ) : rows === null ? (
                <div className="screen__loading" aria-busy="true">
                  <p className="sr-only">Loading…</p>
                  <span className="skeleton" />
                  <span className="skeleton" />
                  <span className="skeleton" />
                </div>
              ) : rows.length === 0 ? (
                <p className="nothing">
                  Nothing yet. When somebody names you in a comment, replies to you or asks you to
                  review a draft, it appears here — and so does what this product notices on its
                  own: a publish that finished or failed, an API change that left a page describing
                  something that no longer works, a page of yours falling due for review, and
                  anything about the workspace itself that somebody has to decide.
                </p>
              ) : (
                <ul className="notiflist">
                  {rows.map((row) => (
                    <li key={row.id} className="notiflist__row">
                      <button
                        className="notiflist__go"
                        onClick={() => {
                          setOpen(false)
                          if (row.pagePath) onOpenPage(row.pagePath, row.branch)
                          else if (row.branch) onOpenDraft(row.branch)
                          /**
                           * And everything else goes to Settings, because
                           * everything else is about the workspace: the
                           * allowance, the plan, the payment, the webhook, the
                           * domain, who owns it. Those kinds carry no page and
                           * no draft, and before this they answered a press
                           * with nothing — a row that looks like a door and is
                           * a wall, in the one panel whose whole promise is
                           * that every row is a door.
                           *
                           * No COUNT in this sentence, deliberately. It said
                           * "five of the fourteen" and there are sixteen kinds
                           * now; the subtitle above rotted the same way twice
                           * and stopped listing them for the same reason. What
                           * matters is that this branch is exhaustive by
                           * shape — page, draft, else Settings — rather than a
                           * list somebody has to remember to extend.
                           */
                          else onOpenSettings(SETTLED_IN[row.kind])
                        }}
                      >
                        <span className="notiflist__kind" data-kind={row.kind}>
                          {KIND[row.kind] ?? 'Update'}
                        </span>
                        <span className="notiflist__subject">{row.subject}</span>
                        <span className="notiflist__when">
                          {new Date(row.createdAt).toLocaleString()}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  )
}
